import { useState } from 'react';
import { api, ApiError, type EventView, type Journal, type MediaCard, type ReferencedBy } from '../api.js';
import { JournalQuote, Loading, MediaStrip, Page, useApi } from '../components.js';
import { href } from '../router.js';

const ROLES = ['arrival', 'detail', 'people', 'departure'];
const KIND_LABEL: Record<string, string> = {
  journal: 'Journal entry', media: 'Photo or clip', incident: 'Incident', alias: 'External link', event: 'Event',
  correction: 'Correction', person: 'Person', place: 'Place', day: 'Day', device: 'Device', selectSet: 'Select set', timeline: 'Timeline',
};

export function EventPage({ exp, id }: { exp: string; id: string }) {
  const { data, error, refetch } = useApi<EventView>(`/api/expeditions/${exp}/events/${id}`);
  const [msg, setMsg] = useState<string | null>(null);
  const [refs, setRefs] = useState<ReferencedBy[] | null>(null);
  const [deleted, setDeleted] = useState(false);
  const back = { href: href.day(exp, id.slice(0, 3)), label: id.slice(0, 3) };
  const historyLink = <a href={href.history(exp, 'event', id)}>Undo delete / history</a>;

  if (deleted) return <Page title={id} back={back}><p role="status">{id} was deleted.</p><p>{historyLink}</p></Page>;
  if (!data) {
    return (
      <Page title={id} back={back}>
        <Loading error={error} />
        {error && <p className="muted">If this event was deleted, its history can bring it back: {historyLink}</p>}
      </Page>
    );
  }
  const e = data.event;

  const run = async (f: () => Promise<unknown>) => {
    try { await f(); setMsg(null); } catch (err) { setMsg((err as ApiError).message); }
  };
  const dropRef = (refId: string) => setRefs((r) => (r ? r.filter((x) => x.id !== refId) : r));
  const unlinkMedia = (m: MediaCard) => run(async () => {
    await api('PATCH', `/api/expeditions/${exp}/records/media/${m.id}`, { patch: { eventIds: m.eventIds.filter((x) => x !== id) }, version: m.version });
    dropRef(m.id);
    refetch();
  });
  const unlinkJournal = (j: Journal) => run(async () => {
    await api('PATCH', `/api/expeditions/${exp}/records/journal/${j.id}`, { patch: { eventIds: (j.eventIds ?? []).filter((x) => x !== id) }, version: j.version });
    dropRef(j.id);
    refetch();
  });
  const del = async () => {
    try {
      await api('DELETE', `/api/expeditions/${exp}/records/event/${id}`);
      setDeleted(true);
    } catch (err) {
      const ae = err as ApiError;
      if (ae.status === 409 && Array.isArray(ae.body.referencedBy)) { setRefs(ae.body.referencedBy as ReferencedBy[]); setMsg(null); }
      else setMsg(ae.message);
    }
  };

  return (
    <Page title={e.title} back={back}>
      <p className="muted">{e.id} · {e.kind ?? 'event'} · {e.origin}{e.publication === 'review' ? ' · ⚑ publication needs review' : ''} · <a href={href.history(exp, 'event', e.id)}>history (v{e.version})</a></p>
      {msg && <p className="error" role="alert">{msg}</p>}
      {refs && refs.length > 0 && (
        <div className="card references" role="alert">
          <strong>Can't delete yet: still linked from</strong>
          <ul>{refs.map((r) => <li key={r.id}>{r.kind ? KIND_LABEL[r.kind] ?? r.kind : 'Link'} {r.kind === 'journal' ? '' : r.id}{r.label ? ` "${r.label}"` : ''}</li>)}</ul>
          <p className="muted">Unlink these below (photos and journal entries have an unlink control), then delete again.</p>
        </div>
      )}
      {e.narrative && <p>{e.narrative}</p>}
      <h2>Coverage</h2>
      <p className="roles">{ROLES.map((r) => <span key={r} className={data.coverageRoles.includes(r) ? 'have' : 'miss'}>{r}</span>)}</p>
      <MediaStrip exp={exp} media={data.media} onUnlink={unlinkMedia} />
      <h2>Journal</h2>
      {data.journal.length ? data.journal.map((j) => <JournalQuote key={j.id} j={j} onUnlink={() => unlinkJournal(j)} />) : <p className="muted">No journal entries.</p>}
      {data.people.length > 0 && <><h2>People</h2><ul className="list">{data.people.map((p) => <li key={p.id}>{p.name} <span className="muted">· {p.role} · <a href={href.history(exp, 'person', p.id)} aria-label={`History of ${p.name}`}>history</a></span></li>)}</ul></>}
      {data.places.length > 0 && <><h2>Places</h2><ul className="list">{data.places.map((p) => <li key={p.id}>{p.name} <span className="muted">· {p.kind} · <a href={href.history(exp, 'place', p.id)} aria-label={`History of ${p.name}`}>history</a></span></li>)}</ul></>}
      {data.incidents.length > 0 && <><h2>Incidents</h2>{data.incidents.map((i) => <p key={i.id}><strong>{i.what}</strong>{i.response ? ` → ${i.response}` : ''}{i.lesson ? ` · Lesson: ${i.lesson}` : ''}</p>)}</>}
      {e.plannedShotRefs?.length ? <><h2>Planned shots (PlanB)</h2><ul className="list">{e.plannedShotRefs.map((s) => <li key={s.ref}>{s.description ?? s.ref} <span className={`outcome ${s.outcome ?? 'open'}`}>{s.outcome ?? 'open'}</span></li>)}</ul></> : null}
      <div className="actions">
        <button type="button" className="danger" onClick={del}>Delete event</button>
      </div>
    </Page>
  );
}
